import * as React from "react"
import { useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { useSignal } from "@preact/signals-react"
import {
	Box, Chip, Divider, Grid, InputAdornment, List, ListItemButton, ListItemText, Paper,
	Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { CardSeeker, IconSeeker } from "@ui-seeker/core"

import { INFOCENTER_ROUTES } from "@constants"
import PageWrapper from "Pages/@PageWrapper"
import {
	GLOSSARY_INTRO, GLOSSARY_SECTIONS, SCORE_COLOR_SCALE, SCORE_COMPOSITION, TREND_LEGEND
} from "./content"


/** Breathing room left above whatever is pinned to the top. */
const GAP = 16

/**
 * How much of the top of the screen the app's header covers.
 *
 * `MaximalLayout` renders it as `position: fixed`, so it overlaps the content instead of
 * pushing it down: without discounting it the pinned column sits underneath the header and a
 * card jumped to lands half hidden behind it. Measured rather than configured — the height
 * comes from `appConfig`, which this app leaves at its default.
 */
const headerOffset = () => {
	const header = document.querySelector('header')
	if (!header) return 0
	const { position } = getComputedStyle(header)
	if (position !== 'fixed' && position !== 'sticky') return 0
	return Math.max(0, Math.round(header.getBoundingClientRect().bottom))
}

/**
 * The nearest ancestor that actually scrolls, falling back to the document.
 *
 * Do not assume: the layout mounts SimpleBar, but only around the side menu — the glossary
 * itself scrolls with the window.
 */
const scrollParentOf = (node) => {
	for (let parent = node.parentElement; parent; parent = parent.parentElement) {
		const { overflowY } = getComputedStyle(parent)
		if (/(auto|scroll)/.test(overflowY) && parent.scrollHeight > parent.clientHeight) return parent
	}
	return document.scrollingElement
}

/** Every word a section can be found by, so the filter matches the body and not only the title. */
const searchableText = (section) => [
	section.title, section.chip, section.lead,
	...section.blocks.map(block => `${block.label} ${block.text}`)
].join(' ').toLowerCase()


export default function InfoCenterKpiGlossaryPage() {

	const props = {
		head: `DIP Insights | ${INFOCENTER_ROUTES.KPI_GLOSSARY_TITLE}`,
		title: INFOCENTER_ROUTES.KPI_GLOSSARY_TITLE,
		subtitle: INFOCENTER_ROUTES.KPI_GLOSSARY_SUBTITLE,
		isEmpty: false,
		actions: ''
	}

	const [searchParams] = useSearchParams()
	const requestedKpi = searchParams.get('kpi')
	const search = useSignal('')
	const activeId = useSignal(GLOSSARY_SECTIONS[0].id)
	const overlap = useSignal(0)

	const term = search.value.trim().toLowerCase()
	const sections = term
		? GLOSSARY_SECTIONS.filter(section => searchableText(section).includes(term))
		: GLOSSARY_SECTIONS

	const topOffset = overlap.value + GAP

	useEffect(() => {
		const measure = () => overlap.value = headerOffset()
		measure()
		window.addEventListener('resize', measure)
		return () => window.removeEventListener('resize', measure)
	}, [])

	// Highlight whichever card crosses the middle of the screen. The band has to match where
	// `scrollTo` parks a card — anchoring it to the top instead marks the *previous* card as
	// active the moment a jump centres the one that was clicked.
	useEffect(() => {
		const visible = new Map()
		const observer = new IntersectionObserver(
			entries => {
				entries.forEach(entry => visible.set(entry.target.id, entry.isIntersecting
					? entry.boundingClientRect.top
					: null))
				const onScreen = [...visible.entries()].filter(([, top]) => top !== null)
				if (onScreen.length) activeId.value = onScreen.sort((a, b) => a[1] - b[1])[0][0]
			},
			{
				// A thin band across the middle of the readable area.
				rootMargin: `-${topOffset + Math.round((window.innerHeight - topOffset) * 0.4)}px 0px -45% 0px`,
				threshold: 0
			}
		)
		sections.forEach(section => {
			const node = document.getElementById(section.id)
			if (node) observer.observe(node)
		})
		return () => observer.disconnect()
	}, [term, topOffset])

	const scrollTo = (id) => {
		activeId.value = id
		const node = document.getElementById(id)
		if (!node) return

		const scroller = scrollParentOf(node)
		const isWindow = scroller === document.scrollingElement
		const rect = node.getBoundingClientRect()
		const top = isWindow
			? rect.top + window.scrollY
			: rect.top - scroller.getBoundingClientRect().top + scroller.scrollTop

		// Centre the card in the readable area instead of pinning it under the header. A card
		// taller than the space left gets no padding, so its title never scrolls out of sight.
		const available = (isWindow ? window.innerHeight : scroller.clientHeight) - topOffset
		const padding = Math.max(0, (available - rect.height) / 2)

		scroller.scrollTo({ top: Math.max(0, top - topOffset - padding), behavior: 'smooth' })
	}

	// Arriving from a `?` on the Digital Shelf table:
	// `/info-center/kpi-glossary?kpi=stock-score`. It waits for the header to be measured, or
	// the jump would land using an offset of zero.
	useEffect(() => {
		if (!requestedKpi || !overlap.value) return
		if (!GLOSSARY_SECTIONS.some(section => section.id === requestedKpi)) return
		const timer = setTimeout(() => scrollTo(requestedKpi), 80)
		return () => clearTimeout(timer)
	}, [requestedKpi, overlap.value])

	return <PageWrapper {...props}>
		<Grid container spacing={4}>
			<Grid item xs={12}>
				{/* MUI's Card clips its children by default, and an `overflow: hidden`
				    ancestor disables `position: sticky` on the index column. */}
				<CardSeeker title="" sx={{ overflow: 'visible' }}>
					<Grid container spacing={3}>

						<Grid item xs={12} md={3}>
							<Box sx={{
								position: 'sticky',
								top: `${topOffset}px`,
								maxHeight: `calc(100vh - ${topOffset + GAP}px)`,
								// The search box and the heading stay put; only the list scrolls.
								display: 'flex',
								flexDirection: 'column',
								overflow: 'hidden'
							}}>
								<TextField
									fullWidth
									size="small"
									placeholder="Search the glossary"
									value={search.value}
									onChange={event => search.value = event.target.value}
									InputProps={{
										startAdornment: <InputAdornment position="start">
											<IconSeeker icon="magnifying-glass" sx={{ fontSize: '0.85rem' }} />
										</InputAdornment>
									}}
									sx={{ mb: 1.5, flexShrink: 0 }}
								/>

								<Typography
									variant="overline"
									sx={{ color: 'text.secondary', letterSpacing: '0.12em', pl: 2, flexShrink: 0 }}
								>
									On this page
								</Typography>

								<Box sx={{ overflowY: 'auto', pr: 0.5 }}>
								<List dense disablePadding>
									{sections.map(section => {
										const isActive = activeId.value === section.id
										return <ListItemButton
											key={section.id}
											selected={isActive}
											onClick={() => scrollTo(section.id)}
											sx={{
												borderRadius: 1,
												borderLeft: '3px solid',
												borderColor: isActive ? `${section.accent}.main` : 'transparent'
											}}
										>
											<IconSeeker
												icon={section.icon}
												sx={{ fontSize: '0.8rem', mr: 1.5, color: `${section.accent}.main` }}
											/>
											<ListItemText
												primary={section.title}
												primaryTypographyProps={{
													variant: 'body2',
													fontWeight: isActive ? 700 : 400
												}}
											/>
										</ListItemButton>
									})}
								</List>
								</Box>

								{!sections.length && <Typography
									variant="body2"
									sx={{ color: 'text.secondary', pl: 2, pt: 1 }}
								>
									Nothing matches “{search.value}”.
								</Typography>}
							</Box>
						</Grid>

						<Grid item xs={12} md={9}>
							{!term && <IntroPanel />}
							{sections.map(section => <GlossaryCard
								key={section.id}
								section={section}
								isActive={activeId.value === section.id}
							/>)}
						</Grid>

					</Grid>
				</CardSeeker>
			</Grid>
		</Grid>
	</PageWrapper>
}


/** The "how to read the table" panel: the text blocks plus the three visual legends. */
function IntroPanel() {
	return <Paper
		variant="outlined"
		sx={{ p: 3, mb: 3, borderRadius: 2, backgroundColor: 'background.neutral' }}
	>
		<Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
			{GLOSSARY_INTRO.title}
		</Typography>

		{GLOSSARY_INTRO.blocks.map(block => <Box key={block.label} sx={{ mb: 2 }}>
			<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{block.label}</Typography>
			<Typography variant="body2" sx={{ fontWeight: 300 }}>{block.text}</Typography>
		</Box>)}

		<Divider sx={{ my: 2.5 }} />

		<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
			What the Digital Shelf Score is made of
		</Typography>
		<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
			<Chip label="Digital Shelf Score" color="primary" sx={{ fontWeight: 700 }} />
			<Typography variant="h6" sx={{ color: 'text.secondary', px: 0.5 }}>=</Typography>
			<Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>average of</Typography>
			{SCORE_COMPOSITION.map(name => <Chip key={name} label={name} variant="outlined" size="small" />)}
		</Box>
		<Typography variant="caption" sx={{ color: 'text.secondary' }}>
			The Organic and Sponsored columns break down Share of Search; they do not enter the average a second time.
		</Typography>

		<Divider sx={{ my: 2.5 }} />

		<Grid container spacing={3}>
			<Grid item xs={12} sm={7}>
				<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
					What the colour of a score means
				</Typography>
				{SCORE_COLOR_SCALE.map(band => <Box
					key={band.range}
					sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}
				>
					<Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: band.color }} />
					<Typography variant="body2" sx={{ fontWeight: 300 }}>{band.range}</Typography>
				</Box>)}
			</Grid>

			<Grid item xs={12} sm={5}>
				<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
					What the arrow means
				</Typography>
				{TREND_LEGEND.map(item => <Box
					key={item.icon}
					sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}
				>
					<IconSeeker icon={item.icon} sx={{ fontSize: '0.85rem', color: item.color, width: 14 }} />
					<Typography variant="body2" sx={{ fontWeight: 300 }}>{item.text}</Typography>
				</Box>)}
				<Typography variant="caption" sx={{ color: 'text.secondary' }}>
					Compared with the previous period of the same length.
				</Typography>
			</Grid>
		</Grid>
	</Paper>
}


/** One KPI: header strip with its icon and category, the lead sentence, then the detail. */
function GlossaryCard({ section, isActive }) {
	return <Paper
		id={section.id}
		variant="outlined"
		sx={{
			p: 3,
			mb: 2,
			borderRadius: 2,
			transition: 'background-color .25s ease, border-color .25s ease, box-shadow .25s ease',
			// A wash of the KPI's own colour, faint enough to read over: it says "this is the one
			// you picked" without competing with the text.
			...isActive && {
				backgroundColor: theme => alpha(theme.palette[section.accent].main, 0.06),
				borderColor: theme => alpha(theme.palette[section.accent].main, 0.45),
				boxShadow: theme => `0 0 0 1px ${alpha(theme.palette[section.accent].main, 0.12)}`
			}
		}}
	>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
			<Box sx={{
				width: 40,
				height: 40,
				borderRadius: '50%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				backgroundColor: `${section.accent}.lighter`,
				color: `${section.accent}.dark`,
				flexShrink: 0
			}}>
				<IconSeeker icon={section.icon} sx={{ fontSize: '1rem' }} />
			</Box>

			<Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
				{section.title}
			</Typography>

			<Chip label={section.chip} size="small" variant="outlined" color={section.accent} />
		</Box>

		<Typography variant="body1" sx={{ fontWeight: 400, mb: 2 }}>
			{section.lead}
		</Typography>

		{section.blocks.map(block => <Box
			key={block.label}
			sx={{ borderLeft: '2px solid', borderColor: 'divider', pl: 2, mb: 1.5 }}
		>
			<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{block.label}</Typography>
			<Typography variant="body2" sx={{ fontWeight: 300 }}>{block.text}</Typography>
		</Box>)}

		{!!section.table && <GlossaryTable table={section.table} />}
	</Paper>
}


function GlossaryTable({ table }) {
	return <Table size="small" sx={{ mt: 2, maxWidth: 640 }}>
		<TableHead>
			<TableRow>
				{table.head.map(cell => <TableCell key={cell} sx={{ fontWeight: 700 }}>{cell}</TableCell>)}
			</TableRow>
		</TableHead>
		<TableBody>
			{table.rows.map((row, index) => <TableRow key={index}>
				{row.map((cell, cellIndex) => <TableCell key={cellIndex}>{cell}</TableCell>)}
			</TableRow>)}
		</TableBody>
	</Table>
}
